import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon,
  FileCode2,
  AlertCircle,
  Download,
  UserSquare2,
  Copy,
  Check,
  Printer,
} from "lucide-react";
import { loadQRCode } from "@/lib/qrcode-lazy";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/vcard-qr-generator";
const TITLE = "vCard QR Code Generator — Save Contacts in One Scan";
const DESC =
  "Free vCard QR generator. Turn name, phone, email and organisation into a scannable QR — runs in your browser, downloads as PNG or SVG.";

const FAQ = [
  {
    q: "What Is a vCard QR Code?",
    a: "It is a QR code that encodes a vCard 3.0 contact record. When someone scans it with their phone camera, they can save your name, phone, email, organisation, website and address straight to their address book in one tap.",
  },
  {
    q: "Will It Work on iPhone and Android?",
    a: "Yes. iOS Camera and Android Camera both recognise vCard payloads natively and prompt the user to add the contact. The same QR works in any QR reader app that supports the vCard spec.",
  },
  {
    q: "Which Fields Are Required?",
    a: "At minimum you need one of name, phone or email — an empty vCard is not useful. Everything else (organisation, title, website, address, note) is optional.",
  },
  {
    q: "vCard 3.0 or 4.0?",
    a: "This generator emits vCard 3.0, which has the broadest support across iOS and Android. vCard 4.0 is supported by fewer scanners and offers no extra benefit for this use case.",
  },
  {
    q: "Is My Contact Data Sent Anywhere?",
    a: "No. The vCard is assembled and encoded into the QR code entirely in your browser using the open-source qrcode library. Nothing is uploaded, logged, or stored on our servers.",
  },
  {
    q: "Why Is My QR Code so Dense?",
    a: "vCards with long addresses, notes and multiple phone numbers produce large payloads. Drop optional fields, or lower the error correction level if you only need it for screens (not print).",
  },
  {
    q: "Can I Add a Photo to a vCard QR Code?",
    a: "No, this generator does not add a photo to the vCard. A photo would have to travel inside the code itself, and even a small image soon exceeds the 2,953 bytes that the largest QR code can hold at error correction level L. Add the photo to the contact on your own phone, or put a profile page in the website field.",
  },
  {
    q: "Does a vCard QR Code Work Offline?",
    a: "Yes. The contact details are stored in the code, so the scanning phone can decode them and save the contact without a data connection. Only the website in the card needs a connection, and only when someone taps it. Creating the code also happens in your browser, with nothing sent to a server.",
  },
  {
    q: "Can I Add a Logo to a vCard QR Code?",
    a: "This generator does not place a logo on the code; it outputs a plain QR code in the two colors you pick. If you add a logo afterwards to the SVG in a design tool, set error correction to H before downloading, keep the logo small and away from the three corner squares, and scan the result before printing. The logo hides modules, and the code only reads if error correction can recover them.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "vCard QR Code Generator", path: PATH },
];

type EcLevel = "L" | "M" | "Q" | "H";

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+0-9 ()\-.]+$/;

/** Escape per vCard 3.0: backslash, comma, semicolon and newline. */
function escapeVcard(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

type VcardInput = {
  firstName: string;
  lastName: string;
  organization: string;
  title: string;
  phoneMobile: string;
  phoneWork: string;
  email: string;
  website: string;
  street: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  note: string;
};

function buildVcard(v: VcardInput): string {
  const lines: string[] = ["BEGIN:VCARD", "VERSION:3.0"];
  const fn = [v.firstName, v.lastName].filter(Boolean).join(" ").trim();
  if (fn) lines.push(`FN:${escapeVcard(fn)}`);
  if (v.firstName || v.lastName) {
    lines.push(`N:${escapeVcard(v.lastName)};${escapeVcard(v.firstName)};;;`);
  }
  if (v.organization) lines.push(`ORG:${escapeVcard(v.organization)}`);
  if (v.title) lines.push(`TITLE:${escapeVcard(v.title)}`);
  if (v.phoneMobile) lines.push(`TEL;TYPE=CELL,VOICE:${escapeVcard(v.phoneMobile)}`);
  if (v.phoneWork) lines.push(`TEL;TYPE=WORK,VOICE:${escapeVcard(v.phoneWork)}`);
  if (v.email) lines.push(`EMAIL;TYPE=INTERNET:${escapeVcard(v.email)}`);
  if (v.website) lines.push(`URL:${escapeVcard(v.website)}`);
  if (v.street || v.city || v.region || v.postcode || v.country) {
    lines.push(
      `ADR;TYPE=WORK:;;${escapeVcard(v.street)};${escapeVcard(v.city)};${escapeVcard(v.region)};${escapeVcard(v.postcode)};${escapeVcard(v.country)}`,
    );
  }
  if (v.note) lines.push(`NOTE:${escapeVcard(v.note)}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

function validateInputs(v: VcardInput, darkColor: string, lightColor: string): string | null {
  const hasName = !!(v.firstName || v.lastName);
  const hasPhone = !!(v.phoneMobile || v.phoneWork);
  if (!hasName && !hasPhone && !v.email) {
    return "Add at least a name, phone number or email — an empty vCard cannot be saved.";
  }
  if (v.phoneMobile && !PHONE_RE.test(v.phoneMobile)) {
    return "Mobile phone may only contain digits, spaces and the characters + ( ) - .";
  }
  if (v.phoneWork && !PHONE_RE.test(v.phoneWork)) {
    return "Work phone may only contain digits, spaces and the characters + ( ) - .";
  }
  if (v.email && !EMAIL_RE.test(v.email)) {
    return "Enter a valid email address (e.g. you@example.com).";
  }
  if (v.website) {
    try {
      new URL(v.website);
    } catch {
      return "Website must be a full URL including https:// (e.g. https://example.com).";
    }
  }
  if (!HEX_COLOR.test(darkColor))
    return "Foreground colour must be a hex value like #0f172a or #000.";
  if (!HEX_COLOR.test(lightColor))
    return "Background colour must be a hex value like #ffffff or #fff.";
  if (darkColor.toLowerCase() === lightColor.toLowerCase())
    return "Foreground and background colours must differ.";
  return null;
}

function safeFilename(v: VcardInput): string {
  const base =
    [v.firstName, v.lastName].filter(Boolean).join("-") || v.organization || v.email || "contact";
  const slug = base.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "contact";
  return `vcard-${slug}`;
}

function VcardQrGeneratorPage() {
  const [v, setV] = useState<VcardInput>({
    firstName: "Read",
    lastName: "Barcode",
    organization: "ReadBarcode",
    title: "Founder",
    phoneMobile: "+1 555 0100",
    phoneWork: "",
    email: "hello@readbarcode.com",
    website: "https://readbarcode.com",
    street: "",
    city: "",
    region: "",
    postcode: "",
    country: "",
    note: "",
  });
  const set =
    <K extends keyof VcardInput>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV((prev) => ({ ...prev, [k]: e.target.value }));

  const [ecLevel, setEcLevel] = useState<EcLevel>("M");
  const [size, setSize] = useState(320);
  const [margin, setMargin] = useState(2);
  const [darkColor, setDarkColor] = useState("#0f172a");
  const [lightColor, setLightColor] = useState("#ffffff");

  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");
  const [copied, setCopied] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const payload = useMemo(() => buildVcard(v), [v]);

  const opts = useMemo(
    () => ({
      errorCorrectionLevel: ecLevel,
      margin,
      width: size,
      color: { dark: darkColor, light: lightColor },
    }),
    [ecLevel, margin, size, darkColor, lightColor],
  );

  useEffect(() => {
    const validationErr = validateInputs(v, darkColor, lightColor);
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const QRCode = await loadQRCode();
        if (canvasRef.current) {
          await QRCode.toCanvas(canvasRef.current, payload, opts);
        }
        const svg = await QRCode.toString(payload, { ...opts, type: "svg" });
        if (cancelled) return;
        setSvgMarkup(svg);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to render vCard QR code.";
        setError(
          msg.toLowerCase().includes("too big")
            ? "vCard is too long for the selected error correction. Drop optional fields (note, address) or lower the error correction level."
            : msg,
        );
        setSvgMarkup("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload, opts, v, darkColor, lightColor]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(v)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(v)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadVcf = () => {
    const blob = new Blob([payload], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(v)}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="vCard QR Code Generator"
      description="Turn your contact details into a scannable QR code. One tap from the camera saves your name, phone, email and more to the address book — no app required. You can download the code as PNG or SVG, save the contact as a .vcf file, or print it on a ready-made contact card."
      crumbs={CRUMBS}
      content={
        <>
          <HowToScanSection />

          <ToolSeoSections
            aboutHeading="About the vCard QR code generator"
            about={
              <>
                <p>
                  The ReadBarcode vCard QR code generator packs your contact details into a single QR
                  code that any modern phone camera can save straight to the address book. Print it on a
                  business card, add it to an email signature, or stick it next to your booth at a
                  conference — one scan, full contact saved.
                </p>
                <p>
                  The payload is a standard <code>vCard 3.0</code> record, the format with the widest
                  support across iOS, Android and desktop contact apps. Generation happens entirely in
                  your browser — your phone number and email never leave your device.
                </p>
              </>
            }
            whenToUse={[
              "Printed business cards — a QR on the back saves typing.",
              "Conference badges and trade-show booths.",
              "Email signatures and PDF brochures.",
              "Onboarding packs — share the account manager's contact in one scan.",
              "Real-estate flyers, restaurant menus, service-provider leaflets.",
              "Personal websites — let visitors save your contact instantly.",
            ]}
            howItWorks={[
              {
                title: "Fill in the basics",
                desc: "Name, phone or email is enough. Add organisation, title and address as needed.",
              },
              {
                title: "Add optional details",
                desc: "Website, work phone, postal address, short note — each one makes the QR denser.",
              },
              {
                title: "Pick error correction",
                desc: "M is the default. Use Q or H if you plan to print small or place a logo overlay.",
              },
              {
                title: "Download PNG, SVG or .vcf",
                desc: "PNG for screens, SVG for crisp print, .vcf for direct email attachments.",
              },
              {
                title: "Test before you print",
                desc: "Scan with the iPhone Camera and Android Camera to confirm the contact saves cleanly.",
              },
            ]}
            whatToAvoid={[
              "Dumping an essay into the note field — the QR turns into a dense block that struggles to scan from a distance.",
              "Skipping the name and only adding a phone — some scanners refuse to save a contact without a display name.",
              "Embedding sensitive private addresses you do not want strangers to scan from your back-of-card QR.",
              "Using a website URL without https:// — older scanners may not recognise it as a link.",
              "Inverting colours (light QR on dark background) — many phone cameras refuse to scan them.",
            ]}
            tips={[
              "Keep the QR at least 25 mm wide when printed on a business card.",
              "Drop fields you do not use — every extra field grows the QR.",
              "Generate a separate QR for personal vs work contact details.",
              "Use the QR Code Reader on this site to confirm the vCard parses correctly before you print a batch.",
              "Pair with the Wi-Fi QR generator on rental welcome cards for a complete one-scan setup.",
            ]}
          />
          <PrivacyNote />
          <VcardSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first">First name</Label>
                <Input
                  id="first"
                  value={v.firstName}
                  onChange={set("firstName")}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last">Last name</Label>
                <Input id="last" value={v.lastName} onChange={set("lastName")} autoComplete="off" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="org">Organisation</Label>
                <Input
                  id="org"
                  value={v.organization}
                  onChange={set("organization")}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Job title</Label>
                <Input id="title" value={v.title} onChange={set("title")} autoComplete="off" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="phoneMobile">Mobile phone</Label>
                <Input
                  id="phoneMobile"
                  type="tel"
                  value={v.phoneMobile}
                  onChange={set("phoneMobile")}
                  placeholder="+1 555 0100"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneWork">Work phone</Label>
                <Input
                  id="phoneWork"
                  type="tel"
                  value={v.phoneWork}
                  onChange={set("phoneWork")}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={v.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={v.website}
                  onChange={set("website")}
                  placeholder="https://example.com"
                  autoComplete="off"
                />
              </div>
            </div>

            <details className="rounded-md border border-border">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                Address
              </summary>
              <div className="space-y-3 border-t border-border p-4">
                <div className="space-y-2">
                  <Label htmlFor="street">Street</Label>
                  <Input id="street" value={v.street} onChange={set("street")} autoComplete="off" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={v.city} onChange={set("city")} autoComplete="off" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Region / state</Label>
                    <Input
                      id="region"
                      value={v.region}
                      onChange={set("region")}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input
                      id="postcode"
                      value={v.postcode}
                      onChange={set("postcode")}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={v.country}
                      onChange={set("country")}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </details>

            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                value={v.note}
                onChange={set("note")}
                rows={2}
                placeholder="Optional — keep short to keep the QR scannable."
              />
            </div>

            <details className="rounded-md border border-border">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                QR appearance
              </summary>
              <div className="space-y-4 border-t border-border p-4">
                <div className="space-y-2">
                  <Label htmlFor="ec">Error correction</Label>
                  <Select value={ecLevel} onValueChange={(val) => setEcLevel(val as EcLevel)}>
                    <SelectTrigger id="ec">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">L — ~7% recovery (smallest)</SelectItem>
                      <SelectItem value="M">M — ~15% recovery (default)</SelectItem>
                      <SelectItem value="Q">Q — ~25% recovery</SelectItem>
                      <SelectItem value="H">H — ~30% recovery (logo overlays)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Size</Label>
                    <span className="text-xs text-muted-foreground">{size}px</span>
                  </div>
                  <Slider
                    min={128}
                    max={1024}
                    step={16}
                    value={[size]}
                    onValueChange={(val) => setSize(val[0])}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Quiet zone</Label>
                    <span className="text-xs text-muted-foreground">{margin} modules</span>
                  </div>
                  <Slider
                    min={0}
                    max={8}
                    step={1}
                    value={[margin]}
                    onValueChange={(val) => setMargin(val[0])}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dark">Foreground</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="dark"
                        type="color"
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="font-mono text-xs"
                        aria-label="Foreground hex"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="light">Background</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="light"
                        type="color"
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="font-mono text-xs"
                        aria-label="Background hex"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div
              className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border p-4"
              style={{ backgroundColor: error ? undefined : lightColor }}
            >
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated vCard QR code preview"
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-muted-foreground">vCard payload</div>
                  <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all text-xs">
                    {payload}
                  </pre>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={copyPayload}
                  disabled={!!error}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button variant="outline" onClick={downloadVcf} disabled={!!error}>
                <UserSquare2 className="h-4 w-4" aria-hidden />
                .vcf
              </Button>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setPrintOpen(true)}
              disabled={!!error || !svgMarkup}
            >
              <Printer className="h-4 w-4" aria-hidden />
              Print / PDF
            </Button>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`vCard QR · ${[v.firstName, v.lastName].filter(Boolean).join(" ") || "contact"}`}
        filename={safeFilename(v)}
        svgMarkup={svgMarkup}
        defaultCaption={[v.firstName, v.lastName].filter(Boolean).join(" ")}
        defaults={{
          layoutKind: "card",
          widthMm: 50,
          sheetPreset: "averyL7160",
          paper: "A4",
          orientation: "portrait",
        }}
        cardTemplate={{
          kind: "vcard-card",
          optionLabel: "Contact card",
          data: {
            firstName: v.firstName,
            lastName: v.lastName,
            title: v.title,
            organization: v.organization,
            phone: v.phoneMobile || v.phoneWork,
            email: v.email,
            website: v.website,
          },
        }}
      />
    </ToolLayout>
  );
}

function HowToScanSection() {
  return (
    <ul>
      {[
        {
          title: "iPhone",
          body: "Open the Camera app, point at the QR, and tap the contact notification to add to Contacts.",
        },
        {
          title: "Android",
          body: "Open the Camera (or Google Lens) on Android 10+, point at the QR, and tap Add contact.",
        },
        {
          title: "Desktop",
          body: "Download the .vcf and double-click it in macOS Contacts, Outlook or Google Contacts — fields import automatically.",
        },
      ].map((s) => (
        <li key={s.title}>
          <strong>{s.title}</strong>
          {` ${s.body}`}
        </li>
      ))}
    </ul>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Your Contact Details Stay on Your Device</h2>
      <p>
        The vCard is assembled and encoded locally with the open-source qrcode library. Nothing is
        uploaded, logged, or saved to our servers.
      </p>
    </>
  );
}

function VcardSections() {
  return (
    <>
      <h2>How Do vCard QR Codes Work?</h2>
      <p>A vCard QR code stores your contact record as plain text inside the code, so the phone that scans it reads the details straight from the printed pattern. No link, server or app sits between the scan and the address book.</p>
      <p>The text follows the vCard 3.0 format: it opens with BEGIN:VCARD and closes with END:VCARD. When a camera app finds that opening line, it offers to create a contact instead of opening a web page.</p>
      <p>Between those two lines, this generator writes one property for each field you fill in:</p>
      <ul>
        <li>FN and N for the display name and the separate first and last name.</li>
        <li>ORG and TITLE for the organization and job title.</li>
        <li>TEL for the mobile and work numbers, plus EMAIL and URL.</li>
        <li>ADR for the work address and NOTE for the note.</li>
      </ul>
      <p>Commas, semicolons and line breaks in your entries are escaped as the format requires, so an address such as Suite 4, Main Street stays in one field.</p>
      <h2>Free vCard QR Code Generator Without Sign-Up</h2>
      <p>This online vCard QR code generator is free and asks for no account, email address or trial. The code and every download are ready on the same page, with no watermark.</p>
      <p>The page itself is not open source, but if you wanted an open source vCard QR code generator, the step that draws the QR code comes from the open-source qrcode JavaScript library.</p>
      <h3>vCard QR Codes That Never Expire</h3>
      <p>A free vCard QR code generator with no expiration has to produce a static code, and that is the only kind this page makes. Your details sit inside the QR pattern, not behind a short link that a service could switch off.</p>
      <p>Printed contact codes are fixed once they leave the printer. When your number changes, generate a new code and reprint; old cards still scan but save the old details.</p>
      <h2>QR Code for Contact Info on a Business Card</h2>
      <p>To put a QR code for contact info on a business card, fill in the fields, test the code, and print it through the contact card layout on this page.</p>
      <ol>
        <li>Enter your name plus a phone number or email, and skip fields the card does not need.</li>
        <li>Scan the preview with the <Link to="/qr-code-reader">QR code reader</Link> and check that each field lands in the right place.</li>
        <li>Open Print / PDF and choose Contact card, which prints the QR beside your name, title, organization, phone, email and website.</li>
        <li>Pick a single card, an Avery L7414 sheet on A4 or an Avery 5371 sheet on US Letter, both with ten cards per page.</li>
        <li>Print at actual size and scan one printed card before cutting the sheet.</li>
      </ol>
      <p>For a code that opens a portfolio or booking page rather than saving a contact, use the <Link to="/qr-code-generator">general QR code generator</Link> instead.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Encode any URL, text or payload with custom colours and error correction.",
    },
    {
      path: "/wifi-qr-code-generator",
      label: "Wi-Fi QR Code Generator",
      desc: "Share Wi-Fi credentials with a one-scan QR — perfect alongside a vCard.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Scan the QR you just generated to verify the vCard parses cleanly.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D barcodes",
      desc: "Why QR is the right format for contact sharing, not a 1D barcode.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/vcard-qr-generator")({
  component: VcardQrGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode vCard QR Code Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
